import React, { useEffect, useState, useCallback } from 'react';
import { CheckCircle, XCircle, X, AlertTriangle, Shield, Clock, Zap } from 'lucide-react';
import { Notification } from '../../lib/types';
import { apiListNotifications, apiUpdateNotification } from '../../lib/api';
import { getOffice } from '../../lib/config';
import { useAuth } from '../../context/AuthContext';

const TYPE_CONFIG = {
  blocker: { icon: <AlertTriangle size={14} />, color: '#E74C3C', bg: '#FEE2E2', label: 'Blocker' },
  approval: { icon: <Shield size={14} />, color: '#3498DB', bg: '#DBEAFE', label: 'Approval' },
  override: { icon: <Clock size={14} />, color: '#F5C542', bg: '#FEF3C7', label: 'Override' },
  error: { icon: <Zap size={14} />, color: '#E74C3C', bg: '#FEE2E2', label: 'Error' },
};

const STATUS_BADGE = {
  pending: { bg: '#FEF3C7', text: '#D97706', label: 'Pending' },
  approved: { bg: '#D1FAE5', text: '#059669', label: 'Approved' },
  rejected: { bg: '#FEE2E2', text: '#DC2626', label: 'Rejected' },
  dismissed: { bg: '#F3F4F6', text: '#9CA3AF', label: 'Dismissed' },
};

/**
 * NotificationsPanel — list of items needing attention.
 * Shows blockers, approvals, override requests, errors.
 * Each item has Approve / Reject / Dismiss action buttons.
 */
export default function NotificationsPanel() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending'>('pending');

  const fetchNotifications = useCallback(() => {
    // Don't fetch if not authenticated
    if (!user) {
      setLoading(false);
      return;
    }
    apiListNotifications()
      .then(setNotifications)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const updateStatus = async (id: string, status: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, status: status as any } : n))
    );
    await apiUpdateNotification(id, status).catch(() => fetchNotifications());
  };

  const filtered = filter === 'all' ? notifications : notifications.filter((n) => n.status === 'pending');
  const pendingCount = notifications.filter((n) => n.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="h-6 w-6 rounded-full border-2 border-gray-200 border-t-gray-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="notifications-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setFilter('pending')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              filter === 'pending' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="notif-filter-pending"
          >
            Pending ({pendingCount})
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              filter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="notif-filter-all"
          >
            All ({notifications.length})
          </button>
        </div>
        <button
          onClick={fetchNotifications}
          className="text-xs text-gray-500 hover:text-gray-700 font-medium"
          data-testid="notif-refresh"
        >
          Refresh
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 py-12">
            <CheckCircle size={36} className="text-green-300 mb-3" />
            <p className="text-sm font-medium text-gray-500">All clear</p>
            <p className="text-xs mt-1">No {filter === 'pending' ? 'pending' : ''} notifications</p>
          </div>
        )}

        {filtered.map((notif) => {
          const tc = TYPE_CONFIG[notif.type] || TYPE_CONFIG.blocker;
          const sc = STATUS_BADGE[notif.status] || STATUS_BADGE.pending;
          const office = notif.office_id ? getOffice(notif.office_id) : null;
          const isPending = notif.status === 'pending';

          return (
            <div
              key={notif.id}
              className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
              data-testid={`notification-item-${notif.id}`}
            >
              <div className="flex items-start gap-3">
                {/* Type icon */}
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                  style={{ backgroundColor: tc.bg, color: tc.color }}
                >
                  {tc.icon}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-semibold text-gray-900 leading-snug">{notif.title}</p>
                    <span
                      className="text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0"
                      style={{ backgroundColor: sc.bg, color: sc.text }}
                    >
                      {sc.label}
                    </span>
                  </div>

                  <p className="text-xs text-gray-500 leading-relaxed mb-2">{notif.description}</p>

                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Office badge */}
                    {office && (
                      <span
                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: `${office.color}20`, color: office.color }}
                      >
                        {office.name}
                      </span>
                    )}
                    {/* Type badge */}
                    <span
                      className="text-[10px] font-bold uppercase tracking-wider"
                      style={{ color: tc.color }}
                    >
                      {tc.label}
                    </span>
                    {/* Timestamp */}
                    <span className="text-[10px] text-gray-400">
                      {new Date(notif.created_at).toLocaleDateString()}
                    </span>
                  </div>

                  {/* Action buttons */}
                  {isPending && (
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => updateStatus(notif.id, 'approved')}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-green-50 text-green-700 hover:bg-green-100 transition-colors border border-green-200"
                        data-testid={`notif-approve-${notif.id}`}
                      >
                        <CheckCircle size={11} /> Approve
                      </button>
                      <button
                        onClick={() => updateStatus(notif.id, 'rejected')}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-red-50 text-red-600 hover:bg-red-100 transition-colors border border-red-200"
                        data-testid={`notif-reject-${notif.id}`}
                      >
                        <XCircle size={11} /> Reject
                      </button>
                      <button
                        onClick={() => updateStatus(notif.id, 'dismissed')}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-50 text-gray-500 hover:bg-gray-100 transition-colors border border-gray-200"
                        data-testid={`notif-dismiss-${notif.id}`}
                      >
                        <X size={11} /> Dismiss
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
